import { useQuery } from '../lib/queryClient';
import { getAiModels, OutputType, LocalModel } from '../endpoints/ai/models_GET.schema';

export const AI_MODELS_QUERY_KEY = ['ai', 'models'] as const;
type UseAiModelsOptions = {
  probeLocal?: boolean;
  enabled?: boolean;
};

/** Default local endpoints to probe (in priority order) */
const DEFAULT_LOCAL_ENDPOINTS = [
  'http://localhost:8080', // Quillworks Caddy proxy
  'http://localhost:9000', // Quillworks FastAPI proxy
  'http://localhost:11434', // Direct Ollama
];

/**
 * Probe a local LLM endpoint directly from the browser.
 * The Cloudflare Worker can't reach localhost — only the browser can.
 */
async function probeEndpoint(endpoint: string, apiKey?: string): Promise<LocalModel[]> {
  const base = endpoint.replace(/\/$/, '');
  const headers: Record<string, string> = {};
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  // Try Ollama /api/tags first
  try {
    const c1 = new AbortController();
    const t1 = setTimeout(() => c1.abort(), 3000);
    const res = await fetch(`${base}/api/tags`, { signal: c1.signal, headers });
    clearTimeout(t1);
    if (res.ok) {
      const data = await res.json();
      if (data?.models && Array.isArray(data.models) && data.models.length > 0) {
        return data.models
          .filter((m: any) => !/cloud/i.test(m.name))
          .map((m: any) => ({
            model: m.name,
            name: m.name,
            size: formatBytes(m.size),
            modified: m.modified_at,
          }));
      }
    }
  } catch {
    /* try next */
  }

  // Fallback: /v1/models (OpenAI-compatible or proxy that returns Ollama format)
  try {
    const c2 = new AbortController();
    const t2 = setTimeout(() => c2.abort(), 3000);
    const res = await fetch(`${base}/v1/models`, { signal: c2.signal, headers });
    clearTimeout(t2);
    if (res.ok) {
      const data = await res.json();
      // OpenAI format: { data: [{ id, ... }] }
      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        return data.data
          .filter((m: any) => !/cloud/i.test(m.id))
          .map((m: any) => ({
            model: m.id,
            name: m.id,
            size: '',
            modified: m.created ? new Date(m.created * 1000).toISOString() : '',
          }));
      }
      // Ollama format returned by proxy: { models: [{ name, size, ... }] }
      if (data?.models && Array.isArray(data.models) && data.models.length > 0) {
        return data.models
          .filter((m: any) => !/cloud/i.test(m.name))
          .map((m: any) => ({
            model: m.name,
            name: m.name,
            size: formatBytes(m.size),
            modified: m.modified_at,
          }));
      }
    }
  } catch {
    /* exhausted */
  }

  return [];
}

/**
 * Try to discover local models by probing endpoints in order.
 * Uses user's configured endpoint first, then falls back to defaults.
 */
/** Check if a URL points to a loopback address */
function isLoopbackUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname);
  } catch {
    return false;
  }
}

/** Allow browser probing only for local/private network hosts. */
function isPrivateProbeTarget(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(host)) return true;
    if (host.startsWith('10.')) return true;
    if (host.startsWith('192.168.')) return true;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return true;
    return false;
  } catch {
    return false;
  }
}

/** Skip loopback probes when on a public HTTPS origin (Chrome Private Network Access blocks them) */
function shouldSkipLoopbackProbes(): boolean {
  try {
    return location.protocol === 'https:' && !isLoopbackUrl(location.href);
  } catch {
    return false;
  }
}

async function discoverLocalModels(
  configuredEndpoint?: string,
  apiKey?: string,
): Promise<{ models: LocalModel[]; endpoint: string } | null> {
  const skipLoopback = shouldSkipLoopbackProbes();

  // Build candidate list: user-configured first, then defaults
  const candidates: Array<{ url: string; key?: string }> = [];
  if (
    configuredEndpoint &&
    isPrivateProbeTarget(configuredEndpoint) &&
    !(skipLoopback && isLoopbackUrl(configuredEndpoint))
  ) {
    candidates.push({ url: configuredEndpoint, key: apiKey });
  }
  // Add defaults (skip if already in list, or skip loopback on production)
  const seen = new Set(candidates.map((c) => c.url.replace(/\/$/, '')));
  for (const url of DEFAULT_LOCAL_ENDPOINTS) {
    if (!seen.has(url) && !(skipLoopback && isLoopbackUrl(url))) {
      candidates.push({ url });
      seen.add(url);
    }
  }

  for (const candidate of candidates) {
    const models = await probeEndpoint(candidate.url, candidate.key);
    if (models.length > 0) {
      return { models, endpoint: candidate.url };
    }
  }
  return null;
}

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

export function useAiModels(options?: UseAiModelsOptions) {
  const shouldProbeLocal = options?.probeLocal === true;
  return useQuery<OutputType, Error>({
    queryKey: [...AI_MODELS_QUERY_KEY, shouldProbeLocal ? 'probe-local' : 'no-probe'],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      // Fetch cloud models from backend + any backend-side local model info
      const result = await getAiModels();

      // If backend already found local models (e.g. running wrangler dev locally), use them
      if (result.localModels && result.localModels.length > 0) {
        return result;
      }

      // Only probe local LLM from the browser if the user has local LLM enabled.
      // Skip probing when disabled to avoid unnecessary network requests.
      if (result.localEnabled && shouldProbeLocal) {
        // Probe local LLM directly from the browser.
        // The CF Worker can't reach localhost, but the browser can.
        // Tries user's configured endpoint first, then common default ports.
        const discovered = await discoverLocalModels(result.localEndpoint, result.localApiKey);
        if (discovered) {
          return {
            ...result,
            localModels: discovered.models,
            localEnabled: true,
            localEndpoint: discovered.endpoint,
          };
        }
      }

      return result;
    },
    // Don't refetch too often as local LLM checks might be slow or heavy
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
}
